import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import {
  type CreateParentWithoutStripeParams,
  useCreateParentWithoutStripeMutation,
} from '~entities/parent/api/create-parent-without-stripe';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Form } from '~shared/shadcn/ui/form';
import type { ParentFormValues } from '~shared/types/schema';
import { parentFormSchema } from '~shared/types/schema';
import { zodResolverWithLogging } from '~shared/utils/zod-resolver-with-logging';

import { Address } from './address';
import { ParentDetails } from './parent-details';

const defaultValues: Partial<ParentFormValues> = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  familyName: '',
  relation: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
};

interface ManualParentEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studioId: string;
  initialData?: Partial<ParentFormValues> | null;
}

export const ManualParentEntryDialog = ({
  open,
  onOpenChange,
  studioId,
  initialData,
}: ManualParentEntryDialogProps) => {
  const mutation = useCreateParentWithoutStripeMutation();
  const [currentInitialData, setCurrentInitialData] = useState(initialData);
  const form = useForm<ParentFormValues>({
    resolver: zodResolverWithLogging(parentFormSchema),
    defaultValues: {
      ...defaultValues,
      ...currentInitialData,
    },
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        ...defaultValues,
        ...initialData,
      });
    }
    setCurrentInitialData(initialData);
  }, [initialData, form]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      form.reset(defaultValues);
      setCurrentInitialData(null);
      form.clearErrors();
    }
    onOpenChange(isOpen);
  };

  const onSubmit = async (data: ParentFormValues) => {
    try {
      await mutation.mutateAsync({
        ...data,
        contactId:
          (currentInitialData &&
            (currentInitialData as { contactId?: string }).contactId) ??
          '',
        studioId,
        familyName: data.lastName,
        registration: true,
      } as CreateParentWithoutStripeParams);

      toast.success('Parent created successfully!');
      handleOpenChange(false);
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as {
          response: { data: { error: { message: string } } };
        };
        toast.error(axiosError.response.data.error.message);
      } else {
        toast.error('An unexpected error occurred');
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-h-[90vh] w-[calc(100vw-2rem)] overflow-y-auto p-4 sm:max-w-3xl sm:p-8'>
        <DialogHeader>
          <DialogTitle className='text-center text-xl'>
            Enter Parent Details
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
            <ParentDetails form={form} />
            <Address form={form} />

            <div className='flex justify-end'>
              <Button
                type='submit'
                className='w-full'
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Creating Parent...
                  </>
                ) : (
                  'Create Parent'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
